/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */

import { Fonts } from './fonts'
import { getBrowserData } from './get-browsers-data'
import { murmurhash3_32_gc } from './murmurhash'
import { probeWebGL } from './probe-webgl'

/**
 * JS library version
 * @const
 * @type {number}
 */
const VERSION = 9

/**
 * Number of milliseconds after which the site probing cache entry expires
 * @const
 * @type {number}
 */
const ONE_SECOND = 1000

/**
 * Session cookie name
 * @const
 * @type {string}
 */
const SESSION_COOKIE = 'sid'

/**
 * Session cookie length
 * @const
 * @type {number}
 */
const SESSION_LENGTH = 24

/**
 * Random number for hash function
 * @const
 * @type {number}
 */
const SEED = 2300322201

export class CheckHeaders {
  sid = ''
  fonts: string[] = []
  localIP: string[] | undefined = []
  version = VERSION
  initPromise: Promise<unknown>
  sidUrl: string
  browser = ''

  constructor(sidUrl: string) {
    this.sidUrl = sidUrl
    this.initPromise = this.init()
  }

  /**
   * Initalize the proxy
   */
  init() {
    return Promise.all([
      this.probeWebRTC(),
      this.probeFonts(),
      this.getCookie(SESSION_COOKIE),
      getBrowserData(),
    ]).then((values) => {
      this.localIP = values[0]
      this.fonts = values[1]
      this.sid = values[2]
      this.setCookie(SESSION_COOKIE, this.sid)
      this.browser = JSON.stringify(values[3])
    })
  }

  /**
   * Get browser fingerprint
   * @param screen
   * @returns {object}
   */
  getBrowserFingerprint() {
    const data = []
    data.push(navigator.userAgent)
    data.push(navigator.languages ? navigator.languages.join(',') : '')
    data.push(navigator.maxTouchPoints ? navigator.maxTouchPoints : '')
    data.push(navigator.language || (navigator as any).userLanguage)
    data.push(navigator.doNotTrack ? navigator.doNotTrack : '')
    data.push(Math.max(window.screen.width, window.screen.height))
    data.push(Math.min(window.screen.width, window.screen.height))
    data.push(window.screen.colorDepth)
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    data.push(new Date(1388530800000).getTimezoneOffset()) // 2014-01-01 00:00:00

    const plugins: string[] = []
    for (let i = 0; i < navigator.plugins.length; i++) {
      const mimetypes = []
      const plugin = navigator.plugins[i]
      for (let p = 0; p < plugin.length; p++) {
        mimetypes.push([plugin[p].suffixes, plugin[p].type].join(':'))
      }

      plugins.push(
        [plugin.name, plugin.description, mimetypes.join(',')].join('$')
      )
    }
    data.push(plugins.join('#'))
    data.push(this.fonts ? this.fonts.join('#') : '')

    const webgl = probeWebGL()
    data.push(webgl.vendor)
    data.push(webgl.renderer)

    const result = data.join('|')

    return {
      len: result.length,
      hash: murmurhash3_32_gc(result, SEED),
    }
  }

  getHeaders() {
    return {
      'X-Fingerprint': JSON.stringify(this.getBrowserFingerprint()),
      'X-Local-IP': JSON.stringify(this.localIP),
      'X-Session': this.sid,
      'X-Version': VERSION.toString(),
      'X-Browser': this.browser,
    }
  }

  /**
   * Get cookie with specified id, relies on localStorage as fallback
   * @param id
   */
  getCookie(id: string): Promise<string> {
    return new Promise((resolve) => {
      // multiple methods: first one working is used
      // 1. localStorage, can throw SecurityException
      try {
        const sid = localStorage.getItem(id)
        if (sid && sid.length === SESSION_LENGTH) {
          return resolve(sid)
        }
      } catch (e) {}

      // 2. Cookies
      const c = document.cookie.split(' ')
      for (let i = 0; i < c.length; i++) {
        const cc = c[i].split('=')
        if (cc[0] === id && cc[1].length === SESSION_LENGTH) {
          return resolve(cc[1])
        }
      }

      // 3. cached image
      // var img = document.createElement('img')
      const img = new Image()
      img.crossOrigin = ''
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let sid = ''
        const ctx = canvas.getContext && canvas.getContext('2d')
        if (ctx) {
          canvas.width = 4
          canvas.height = 4
          ctx.drawImage(img, 0, 0)
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          for (let i = 0; i < 32; i += 4) {
            // read only RGB components, ignore alpha
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            sid += (data[i] & 0x1f).toString(32) // extract right 5 bits
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            sid += (data[i + 1] & 0x1f).toString(32)
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            sid += (data[i + 2] & 0x1f).toString(32)
          }
        } else {
          // 4. random string
          for (let i = 0; i < SESSION_LENGTH; i++) {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            sid += Math.floor(Math.random() * 32).toString(32)
          }
        }

        return resolve(sid)
      }
      img.src = this.sidUrl
    })
  }

  /**
   * Set cookie with specified id, also uses localStorage
   * @param {string} id
   * @param {string} val
   */
  setCookie(id: string, val: string) {
    try {
      localStorage.setItem(id, val)
    } catch (e) {}
    document.cookie = `${id}=${val} expires=Fri, 31 Dec 9999 23:59:59 GMT path=/`
  }

  probeWebRTC(): Promise<string[] | undefined> {
    const iframe = window.document.createElement('iframe')
    iframe.style.display = 'none'
    document.body.appendChild(iframe)

    return new Promise((resolve) => {
      // compatibility for firefox and chrome
      let PeerConnection =
        window.RTCPeerConnection ||
        (window as any).mozRTCPeerConnection ||
        (window as any).webkitRTCPeerConnection

      // bypass naive webrtc blocking using an iframe. See https://github.com/diafygi/webrtc-ips
      if (!PeerConnection) {
        const win = iframe.contentWindow

        PeerConnection =
          (win as any).RTCPeerConnection ||
          (win as any).mozRTCPeerConnection ||
          (win as any).webkitRTCPeerConnection
      }

      const is_linux =
        /Linux/i.test(navigator.userAgent) &&
        !/Android/i.test(navigator.userAgent)

      if (
        is_linux ||
        !PeerConnection ||
        !PeerConnection.prototype.createDataChannel
      ) {
        iframe.remove()

        return resolve(undefined)
      }

      const localIP: string[] = []

      const timeoutId = setTimeout(() => {
        iframe.remove()
        resolve(localIP)
      }, ONE_SECOND)

      const addIP = (ip: string) => {
        if (ip === '0.0.0.0') {
          return
        }
        if (localIP.indexOf(ip) === -1) {
          localIP.push(ip)
        }
      }

      const parse = (text?: string) => {
        const items = text?.split('\r\n') ?? []

        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          let parts: string[]

          if (
            item.indexOf('a=candidate') !== -1 ||
            // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
            item.match(/^candidate:\d+/)
          ) {
            const parts = item.split(' ')
            if (parts[7] === 'host') {
              addIP(parts[4])
            }
          } else if (item.indexOf('c=') !== -1) {
            parts = item.split(' ')
            addIP(parts[2])
          }
        }
      }

      const pc = new PeerConnection({
        iceServers: [],
      })
      pc.createDataChannel('', { reliable: false } as any)
      pc.onicecandidateerror = (e) => {
        // eslint-disable-next-line no-console
        console.log('ICE candidate error:', e)
        iframe.remove()

        return resolve(localIP)
      }
      pc.onicecandidate = (e) => {
        if (!e || !e.candidate) {
          clearTimeout(timeoutId)
          resolve(localIP)

          return
        }
        parse(e.candidate.candidate)
      }

      pc.createOffer()
        .then((desc) => {
          parse(desc.sdp)
          pc.setLocalDescription(desc)
        })
        .catch(() => {
          clearTimeout(timeoutId)
          resolve(localIP)
        })
    })
  }

  probeFonts(): Promise<string[]> {
    return new Promise((resolve) => {
      window.onload = () => {
        resolve(Fonts.detect())
      }
      setTimeout(() => {
        resolve([])
      }, ONE_SECOND)
    })
  }
}
